import { parsePath, createPath } from 'history';

/* ══════════════════════════════════════════════════════════════════════════
 * Public-domain deployment mapping.
 *
 * In production each brand storefront is reverse-proxied to a sub-path of its
 * own public domain, which must behave exactly like the internal
 * `/clothing/[brand]` base the SPA is built around:
 *
 *   https://www.abcgruppen.se/merch     ≡ /clothing/abc-se   (Apache proxy)
 *   https://clseifert.com/dk/merch      ≡ /clothing/cls-dk   (nginx proxy)
 *   https://clseifert.com/sv/merch      ≡ /clothing/cls-se   (nginx proxy)
 *   https://clseifert.com/fi/merch      ≡ /clothing/cls-fi   (Cloudflare Worker, 2026-10-06)
 *
 * The SPA keeps ALL internal routing/navigation on `/clothing/[brand]` paths;
 * a translating history (see index.js) maps public↔internal at the address-bar
 * boundary. This module resolves which deployment (if any) the current page is
 * served under and provides the path translators + the per-deployment API
 * origin. On the legacy host (clothing-new.clseifert.com) and localhost no
 * mapping matches and everything behaves exactly as before.
 * ══════════════════════════════════════════════════════════════════════════ */

/*
 * apiBase is a SAME-ORIGIN path (`/merch-api`), not the be.* origin: the brand
 * domain proxies forward it to the backend (see docs/deployment-proxies.md).
 * Same-origin API calls need no CORS and — critically — no OPTIONS preflight
 * (the X-Cart-Token / X-Order-Token headers force one on every cross-origin
 * URL), and they reuse the page's already-open connection. A relative base
 * also stays same-origin on both the www and bare-domain variants.
 * REQUIRES the `/merch-api` proxy rule to be live on the domain BEFORE this
 * build is released there.
 */
const DEPLOYMENTS = [
  {
    hosts: ['www.abcgruppen.se', 'abcgruppen.se'],
    publicPrefix: '/merch',
    internalBase: '/clothing/abc-se',
    apiBase: '/merch-api',
  },
  {
    hosts: ['clseifert.com', 'www.clseifert.com'],
    publicPrefix: '/dk/merch',
    internalBase: '/clothing/cls-dk',
    apiBase: '/merch-api',
  },
  {
    hosts: ['clseifert.com', 'www.clseifert.com'],
    publicPrefix: '/sv/merch',
    internalBase: '/clothing/cls-se',
    apiBase: '/merch-api',
  },
  {
    hosts: ['clseifert.com', 'www.clseifert.com'],
    publicPrefix: '/fi/merch',
    internalBase: '/clothing/cls-fi',
    apiBase: '/merch-api',
  },
];

/**
 * Resolve the deployment for a (hostname, pathname) pair. Longest-prefix wins
 * so `/dk/merch` and `/sv/merch` coexist on the same host. Exported for tests.
 */
export function resolveDeployment(hostname, pathname) {
  const host = String(hostname || '').toLowerCase();
  const path = String(pathname || '/');
  let best = null;
  for (const d of DEPLOYMENTS) {
    if (!d.hosts.includes(host)) continue;
    if (path === d.publicPrefix || path.startsWith(`${d.publicPrefix}/`)) {
      if (!best || d.publicPrefix.length > best.publicPrefix.length) best = d;
    }
  }
  return best;
}

/** The active deployment for this page load (null on legacy host / dev). */
export const deployment =
  typeof window !== 'undefined'
    ? resolveDeployment(window.location.hostname, window.location.pathname)
    : null;

/** Public address-bar pathname → internal `/clothing/[brand]` pathname. */
export function toInternal(pathname, dep = deployment) {
  if (!dep || !pathname) return pathname;
  if (pathname === dep.publicPrefix || pathname === `${dep.publicPrefix}/`) {
    return dep.internalBase;
  }
  if (pathname.startsWith(`${dep.publicPrefix}/`)) {
    return dep.internalBase + pathname.slice(dep.publicPrefix.length);
  }
  return pathname;
}

/** Internal `/clothing/[brand]` pathname → public address-bar pathname.
 *  Paths outside the mapped brand are left untouched (cross-brand links are
 *  out of scope on the public domains). */
export function toPublic(pathname, dep = deployment) {
  if (!dep || !pathname) return pathname;
  if (pathname === dep.internalBase || pathname === `${dep.internalBase}/`) {
    return dep.publicPrefix;
  }
  if (pathname.startsWith(`${dep.internalBase}/`)) {
    return dep.publicPrefix + pathname.slice(dep.internalBase.length);
  }
  return pathname;
}

/** Translate a `to` value (string or partial path object) internal → public. */
export function toPublicTo(to, dep = deployment) {
  if (!dep) return to;
  const path = typeof to === 'string' ? parsePath(to) : to;
  const translated = { ...path, pathname: path.pathname ? toPublic(path.pathname, dep) : path.pathname };
  return typeof to === 'string' ? createPath(translated) : translated;
}

/**
 * Href helper for the few direct `window.location.href = '/clothing/…'`
 * writes that bypass the router: returns the public path on a mapped
 * deployment, the internal path everywhere else.
 */
export function publicHref(internalPath) {
  return toPublic(internalPath);
}
